import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Input, Select, DatePicker, Radio, Checkbox, Breadcrumb, Row, Col, Spin, Alert } from 'antd';
import { InfoCircleOutlined, CloseOutlined, PlusOutlined, CloseCircleOutlined, HolderOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import noticeService from '../services/notices';
import { showConfirmModal, getMessage } from '../components/PopupModal';
import authService from '../services/auth';
import NoticePreviewModal from '../components/NoticePreviewModal';
import {
  NoticeItem,
  UpdateNoticeRequest,
  NoticeContentItem,
  NoticeLayoutType,
  NoticeExposureType
} from '../types/notice';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface SortableContentSectionProps {
  section: NoticeContentItem & { key: number };
  index: number;
  subtitleErrors: Record<number, string>;
  detailErrors: Record<number, string>;
  contentSections: (NoticeContentItem & { key: number })[];
  updateContentSection: (key: number, field: 'subtitle' | 'detail', value: string, element?: HTMLTextAreaElement) => void;
  removeContentSection: (key: number) => void;
}

const SortableContentSection: React.FC<SortableContentSectionProps> = ({
  section,
  index,
  subtitleErrors,
  detailErrors,
  contentSections,
  updateContentSection,
  removeContentSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: '16px',
    border: '1px solid #303030',
    borderRadius: '6px',
    padding: '16px',
    backgroundColor: '#1f1f1f',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: '12px', paddingTop: '8px', display: 'flex', alignItems: 'center' }}>
          <HolderOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
        </div>
        <div style={{ flex: 1, maxWidth: '58ch' }}>
          <Input
            placeholder="소제목을 입력하세요."
            value={section.subtitle}
            onChange={(e) => updateContentSection(section.key, 'subtitle', e.target.value)}
            maxLength={36}
            showCount
            status={subtitleErrors[section.key] ? 'error' : ''}
          />
          {subtitleErrors[section.key] && (
            <div style={{ marginTop: '4px' }}>
              <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                {subtitleErrors[section.key]}
              </span>
            </div>
          )}
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => removeContentSection(section.key)}
          style={{ color: '#a6a6a6', marginLeft: 'auto' }}
          disabled={contentSections.length <= 1}
        />
      </div>
      <div style={{ marginLeft: '28px', marginBottom: '12px' }}>
        <TextArea
          rows={2}
          placeholder="내용을 입력하세요."
          value={section.detail}
          onChange={(e) => updateContentSection(section.key, 'detail', e.target.value, e.target as HTMLTextAreaElement)}
          onKeyDown={(e) => {
            const value = (e.target as HTMLTextAreaElement).value;
            const lineCount = (value.match(/\n/g) || []).length;
            if (e.key === 'Enter' && lineCount >= 1) {
              e.preventDefault();
              getMessage().warning('최대 2줄까지만 입력 가능합니다.');
            }
          }}
          style={{ maxWidth: '42ch', resize: 'none' }}
          maxLength={54}
          showCount
          status={detailErrors[section.key] ? 'error' : ''}
        />
        {detailErrors[section.key] && (
          <div style={{ marginTop: '4px' }}>
            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
              {detailErrors[section.key]}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const NoticeEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [notice, setNotice] = useState<NoticeItem | null>(null);
  const [contentSections, setContentSections] = useState<(NoticeContentItem & { key: number })[]>([]);
  const [titleError, setTitleError] = useState<string>('');
  const [subtitleErrors, setSubtitleErrors] = useState<Record<number, string>>({});
  const [detailErrors, setDetailErrors] = useState<Record<number, string>>({});
  const [additionalInfoError, setAdditionalInfoError] = useState<string>('');
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<NoticeItem | null>(null);
  const [isAlwaysVisible, setIsAlwaysVisible] = useState(false);
  const [isModified, setIsModified] = useState(false);

  useNavigationBlocker(isModified, '/notices');

  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.role === 'super_admin';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (id) {
      loadNoticeData(Number(id));
    }
  }, [id]);

  const loadNoticeData = async (noticeId: number) => {
    try {
      setInitialLoading(true);
      const noticeData = await noticeService.getNoticeDetail(noticeId);
      setNotice(noticeData);
      setApprovalStatus(noticeData.approvalStatus || 'pending');
      setRejectionReason(noticeData.rejectionReason || '');

      // 상시 노출 여부 확인 (endDate가 50년 이상 미래이면 상시 노출)
      const checkAlwaysVisible = (endDate: string): boolean => {
        try {
          const end = new Date(endDate);
          const now = new Date();
          const fiftyYearsFromNow = new Date();
          fiftyYearsFromNow.setFullYear(now.getFullYear() + 50);
          return end >= fiftyYearsFromNow;
        } catch {
          return false;
        }
      };

      const alwaysVisible = checkAlwaysVisible(noticeData.endDate);
      setIsAlwaysVisible(alwaysVisible);

      // 폼 필드 초기값 설정
      form.setFieldsValue({
        layout: noticeData.layout,
        exposure: noticeData.exposure,
        exposurePeriod: alwaysVisible ? null : [dayjs(noticeData.startDate), dayjs(noticeData.endDate)],
        title: noticeData.title,
        additionalInfo: noticeData.additionalInfo || ''
      });

      // 컨텐츠 섹션 설정
      const sectionsWithKeys = noticeData.contents.map((content, index) => ({
        key: index + 1,
        subtitle: content.subtitle,
        detail: content.detail
      }));
      setContentSections(sectionsWithKeys);

    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
      getMessage().error('공지사항 정보를 불러오는데 실패했습니다.');
      navigate('/notices');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          // navigate 전에 isModified를 false로 설정하여 useNavigationBlocker를 비활성화
          setIsModified(false);
          // setTimeout으로 상태 업데이트가 완료된 후 네비게이션 실행
          setTimeout(() => {
            navigate(`/notices/detail/${id}`);
          }, 0);
        },
      });
    } else {
      navigate(`/notices/detail/${id}`);
    }
  };

  const renderApprovalAlert = () => {
    let alertMessage = '';
    let alertType: 'info' | 'warning' | 'error' = 'info';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'approved':
        alertMessage = '승인 완료된 콘텐츠입니다.';
        alertType = 'info';
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        alertType = 'error';
        icon = <CloseCircleOutlined />;
        break;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}
      />
    );
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value || value.trim() === '') {
      setTitleError('제목을 입력해주세요.');
    } else if (value.length > 17) {
      setTitleError('제목은 17자를 초과할 수 없습니다.');
    } else {
      setTitleError('');
    }
    setIsModified(true);
  };

  // TextArea 너비 기준 자동 줄바꿈 처리
  const insertAutoLineBreaks = (text: string, element: HTMLTextAreaElement, maxWidth: number): string => {
    const computedStyle = window.getComputedStyle(element);
    const font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text;

    context.font = font;

    const lines: string[] = [];
    const paragraphs = text.split('\n');

    paragraphs.forEach((paragraph) => {
      if (paragraph === '') {
        lines.push('');
        return;
      }

      let currentLine = '';
      const words = paragraph.split('');

      for (const char of words) {
        const testLine = currentLine + char;
        const metrics = context.measureText(testLine);

        if (metrics.width > maxWidth && currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = char;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine.length > 0) {
        lines.push(currentLine);
      }
    });

    return lines.join('\n');
  };

  const handleAdditionalInfoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value;

    // 58ch 너비 기준으로 자동 줄바꿈 처리
    const element = e.target;
    const computedStyle = window.getComputedStyle(element);
    const fontSize = parseFloat(computedStyle.fontSize);
    const maxWidth = fontSize * 58; // 58ch를 픽셀로 변환

    value = insertAutoLineBreaks(value, element, maxWidth);

    // 줄바꿈이 2개를 초과하면 3줄로 제한
    const lineCount = (value.match(/\n/g) || []).length;
    if (lineCount > 2) {
      const limitedLines = value.split('\n').slice(0, 3);
      value = limitedLines.join('\n');
    }

    if (value.length > 120) {
      setAdditionalInfoError('추가 안내는 120자를 초과할 수 없습니다.');
    } else {
      setAdditionalInfoError('');
    }
    form.setFieldsValue({ additionalInfo: value });
    setIsModified(true);
  };

  const handleSubmit = async () => {
    if (!notice) return;

    try {
      const values = await form.validateFields();

      // 제목 필수 입력 체크
      if (!values.title || values.title.trim() === '') {
        setTitleError('제목을 입력해주세요.');
        getMessage().error('필수 항목을 모두 입력해주세요.');
        return;
      }

      // 글자수 제한 체크
      if (titleError) {
        getMessage().error('제목의 글자수를 확인해주세요.');
        return;
      }

      // 폼 검증
      if (contentSections.length === 0) {
        getMessage().error('내용을 최소 1개 이상 입력해주세요.');
        return;
      }

      // 소제목/세부정보 필수 입력 체크
      let hasEmptyFields = false;
      const newSubtitleErrors: Record<number, string> = {};
      const newDetailErrors: Record<number, string> = {};

      contentSections.forEach(section => {
        if (!section.subtitle || section.subtitle.trim() === '') {
          newSubtitleErrors[section.key] = '소제목을 입력해주세요.';
          hasEmptyFields = true;
        }
        if (!section.detail || section.detail.trim() === '') {
          newDetailErrors[section.key] = '세부정보를 입력해주세요.';
          hasEmptyFields = true;
        }
      });

      if (hasEmptyFields) {
        setSubtitleErrors(newSubtitleErrors);
        setDetailErrors(newDetailErrors);
        getMessage().error('필수 항목을 모두 입력해주세요.');
        return;
      }

      // 글자수 제한 체크
      if (Object.keys(subtitleErrors).length > 0) {
        getMessage().error('소제목의 글자수를 확인해주세요.');
        return;
      }

      if (Object.keys(detailErrors).length > 0) {
        getMessage().error('내용의 글자수를 확인해주세요.');
        return;
      }

      if (additionalInfoError) {
        getMessage().error('추가 안내의 글자수를 확인해주세요.');
        return;
      }

      // 상시 노출이 아닌 경우에만 노출 기간 검증
      if (!isAlwaysVisible) {
        if (!values.exposurePeriod || values.exposurePeriod.length !== 2) {
          getMessage().error('노출 기간을 선택해주세요.');
          return;
        }
      }

      // 내용 검증
      const errors = noticeService.validateNoticeData({
        title: values.title,
        startDate: isAlwaysVisible ? dayjs().format('YYYY-MM-DD') : values.exposurePeriod[0].format('YYYY-MM-DD'),
        endDate: isAlwaysVisible ? dayjs().add(100, 'year').format('YYYY-MM-DD') : values.exposurePeriod[1].format('YYYY-MM-DD'),
        contents: contentSections.map(({ key, ...content }) => content)
      });

      if (errors.length > 0) {
        getMessage().error(errors[0]);
        return;
      }

      // 수퍼관리자인 경우 자동 승인 확인 팝업 표시
      if (isSuperAdmin) {
        showConfirmModal({
          title: '알림',
          content: '저장 시 콘텐츠가 자동 승인됩니다.\n저장하시겠습니까?',
          okText: '확인',
          cancelText: '취소',
          onConfirm: async () => {
            await performUpdate(values);
          },
        });
      } else {
        // 일반 관리자는 바로 저장
        await performUpdate(values);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        getMessage().error('필수 입력 항목을 확인해주세요.');
      } else {
        console.error('공지사항 수정 실패:', error);
        getMessage().error('공지사항 수정에 실패했습니다.');
      }
    }
  };

  const performUpdate = async (values: any) => {
    try {
      if (!notice) return;

      setLoading(true);

      const updateData: UpdateNoticeRequest = {
        id: notice.id,
        title: values.title,
        layout: values.layout,
        exposure: values.exposure,
        startDate: isAlwaysVisible ? dayjs().format('YYYY-MM-DD') : values.exposurePeriod[0].format('YYYY-MM-DD'),
        endDate: isAlwaysVisible ? dayjs().add(100, 'year').format('YYYY-MM-DD') : values.exposurePeriod[1].format('YYYY-MM-DD'),
        contents: contentSections.map(({ key, ...content }) => content),
        additionalInfo: values.additionalInfo || undefined,
        // 수퍼 관리자는 자동 승인, 일반 관리자는 승인 대기
        approvalStatus: isSuperAdmin ? 'approved' : 'pending'
      };

      await noticeService.updateNotice(updateData);
      getMessage().success('성공적으로 저장되었습니다.');

      // navigate 전에 isModified를 false로 설정하여 useNavigationBlocker를 비활성화
      setIsModified(false);
      // setTimeout으로 상태 업데이트가 완료된 후 네비게이션 실행
      setTimeout(() => {
        navigate(`/notices/detail/${id}`);
      }, 0);
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      getMessage().error('공지사항 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const removeContentSection = (key: number) => {
    setContentSections(prev => prev.filter(section => section.key !== key));
  };

  const addContentSection = () => {
    if (contentSections.length >= 3) {
      getMessage().warning('내용은 최대 3개까지 추가할 수 있습니다.');
      return;
    }

    const newKey = contentSections.length > 0 ? Math.max(...contentSections.map(s => s.key)) + 1 : 1;
    setContentSections(prev => [
      ...prev,
      { key: newKey, subtitle: '', detail: '' }
    ]);
  };

  const updateContentSection = (key: number, field: 'subtitle' | 'detail', value: string, element?: HTMLTextAreaElement) => {
    // 필수 입력 및 글자수 체크
    if (field === 'subtitle') {
      if (!value || value.trim() === '') {
        setSubtitleErrors(prev => ({ ...prev, [key]: '소제목을 입력해주세요.' }));
      } else if (value.length > 36) {
        setSubtitleErrors(prev => ({ ...prev, [key]: '소제목은 36자를 초과할 수 없습니다.' }));
      } else {
        setSubtitleErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
      }
    } else if (field === 'detail') {
      // 58ch 너비 기준으로 자동 줄바꿈 처리
      if (element) {
        const computedStyle = window.getComputedStyle(element);
        const fontSize = parseFloat(computedStyle.fontSize);
        const maxWidth = fontSize * 58;
        value = insertAutoLineBreaks(value, element, maxWidth);
      }

      // 2줄 제한
      const lineCount = (value.match(/\n/g) || []).length;
      if (lineCount > 1) {
        const limitedLines = value.split('\n').slice(0, 2);
        value = limitedLines.join('\n');
      }

      if (!value || value.trim() === '') {
        setDetailErrors(prev => ({ ...prev, [key]: '세부정보를 입력해주세요.' }));
      } else if (value.length > 54) {
        setDetailErrors(prev => ({ ...prev, [key]: '세부정보는 54자를 초과할 수 없습니다.' }));
      } else {
        setDetailErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
      }
    }

    setIsModified(true);
    setContentSections(prev =>
      prev.map(section =>
        section.key === key ? { ...section, [field]: value } : section
      )
    );
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setContentSections((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handlePreview = () => {
    if (!notice) return;

    const values = form.getFieldsValue();

    // 현재 폼 데이터로 임시 NoticeItem 객체 생성
    const tempNotice: NoticeItem = {
      ...notice,
      title: values.title || notice.title,
      layout: values.layout || notice.layout,
      exposure: values.exposure || notice.exposure,
      startDate: values.exposurePeriod?.[0]?.format('YYYY-MM-DD') || notice.startDate,
      endDate: values.exposurePeriod?.[1]?.format('YYYY-MM-DD') || notice.endDate,
      contents: contentSections.map(({ key, ...content }) => content),
      additionalInfo: values.additionalInfo || undefined
    };

    setPreviewData(tempNotice);
    setPreviewVisible(true);
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!notice) {
    return null;
  }

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 브레드크럼 */}
        <Breadcrumb
          items={[
            { title: '콘텐츠 관리' },
            { title: '건물 안내' },
            { title: '공지사항', href: '/notices' },
            { title: '공지사항 수정' }
          ]}
          style={{ marginBottom: '8px' }}
        />

        {/* 페이지 제목 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            공지사항 수정
          </Title>
          <Button onClick={handlePreview}>미리보기</Button>
        </div>

        {renderApprovalAlert()}

        <Form
          form={form}
          layout="vertical"
          style={{ width: '100%' }}
        
        onValuesChange={() => setIsModified(true)}
      >
          {/* 기본 정보 */}
          <Card title="기본 정보" style={{ marginBottom: '24px' }}>
            <Row gutter={[12, 8]}>
              <Col span={6}>
                <Form.Item
                  label="레이아웃"
                  name="layout"
                >
                  <Select placeholder="레이아웃을 선택하세요" 
                  disabled
                  value="-"
                  >
                    <Option key="-" value="layout2">-</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 8]}>
              <Col span={6}>
                <Form.Item
                  label={<span>노출여부 <span style={{ color: 'red' }}>*</span></span>}
                  name="exposure"
                  rules={[
                    { required: false, message: '노출 여부를 선택해주세요.' }
                  ]}
                >
                  <Radio.Group>
                    <Radio value="show">노출</Radio>
                    <Radio value="hide">비노출</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]} style={{ alignItems: 'flex-end' }}>
              <Col span={6}>
                <Form.Item
                  label={<span>노출 기간 <span style={{ color: 'red' }}>*</span></span>}
                  name="exposurePeriod"
                  rules={[
                    { required: false, message: '노출 기간을 선택해주세요.' }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <RangePicker
                    format="YYYY.MM.DD"
                    disabled={isAlwaysVisible}
                    style={{ width: '100%', background: '#141414', border: '1px solid #303030' }}
                  />
                </Form.Item>
              </Col>
              <Col span={2}>
                <div style={{ paddingBottom: '8px' }}>
                  <Checkbox
                    checked={isAlwaysVisible}
                    onChange={(e) => {
                      setIsAlwaysVisible(e.target.checked);
                      if (e.target.checked) {
                        form.setFieldsValue({ exposurePeriod: null });
                      }
                    }}
                  >
                    상시 노출
                  </Checkbox>
                </div>
              </Col>
              <Col span={16}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    기간이 종료되면 자동 비공개 처리됩니다.
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>

          {/* 상세 내용 */}
          <Card title="상세 내용" style={{ marginBottom: '24px' }}>
            <Form.Item
              label={<span>제목 <span style={{ color: 'red' }}>*</span></span>}
              name="title"
              validateStatus={titleError ? 'error' : ''}
              help={titleError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{titleError}</span> : ''}
              rules={[
                { required: false, message: '제목을 입력해주세요.' },
                { max: 17, message: '제목은 17자를 초과할 수 없습니다.' }
              ]}
              style={{ marginBottom: '24px' }}
            >
              <Input
                placeholder="공지사항 제목을 입력하세요"
                maxLength={17}
                showCount
                onChange={handleTitleChange}
              />
            </Form.Item>

            <Form.Item
              label={<span>내용 <span style={{ color: 'red' }}>*</span></span>}
              style={{ marginBottom: '16px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  소제목과 세부정보를 한 묶음으로 하여 여러 개의 내용을 추가할 수 있습니다.
                </Text>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={contentSections.map(s => s.key)} strategy={verticalListSortingStrategy}>
                  {contentSections.map((section, index) => (
                    <SortableContentSection
                      key={section.key}
                      section={section}
                      index={index}
                      subtitleErrors={subtitleErrors}
                      detailErrors={detailErrors}
                      contentSections={contentSections}
                      updateContentSection={updateContentSection}
                      removeContentSection={removeContentSection}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <div style={{
                border: contentSections.length >= 3 ? '1px dashed #595959' : '1px dashed #F24822',
                borderRadius: '6px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={addContentSection}
                  style={{
                    color: contentSections.length >= 3 ? '#595959' : '#F24822',
                    cursor: contentSections.length >= 3 ? 'not-allowed' : 'pointer'
                  }}
                  disabled={contentSections.length >= 3}
                >
                  내용 추가 ({contentSections.length}/3)
                </Button>
              </div>
            </Form.Item>
          </Card>

          {/* 기타 정보 */}
          <Card title="기타 정보" style={{ marginBottom: '32px' }}>
            <Form.Item
              label="추가 안내"
              name="additionalInfo"
              validateStatus={additionalInfoError ? 'error' : ''}
              help={additionalInfoError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{additionalInfoError}</span> : ''}
            >
              <TextArea
                rows={3}
                showCount
                maxLength={120}
                placeholder="추가적인 안내사항이 있다면 입력해주세요."
                onChange={handleAdditionalInfoChange}
                onKeyDown={(e) => {
                  const value = (e.target as HTMLTextAreaElement).value;
                  const lineCount = (value.match(/\n/g) || []).length;
                  // Enter 키를 누르고 이미 2개의 줄바꿈이 있으면 (총 3줄) 입력 방지
                  if (e.key === 'Enter' && lineCount >= 2) {
                    e.preventDefault();
                    getMessage().warning('최대 3줄까지만 입력 가능합니다.');
                  }
                }}
                style={{ maxWidth: '58ch', resize: 'none' }}
              />
            </Form.Item>
          </Card>

          {/* 액션 버튼 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
            <Button size="large" onClick={handleCancel} disabled={loading}>
              취소
            </Button>
            <Button type="primary" size="large" onClick={handleSubmit} loading={loading}>
              수정 완료
            </Button>
          </div>
        </Form>

        {/* 미리보기 모달 */}
        <NoticePreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          notice={previewData}
        />
      </Space>
    </Spin>
  );
};

export default NoticeEdit;